import { Box, Button, Flex, Heading, Image, Link, SimpleGrid, Text } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import ChainIndicators from 'ui/home/indicators/ChainIndicators';
import LatestBlocks from 'ui/home/LatestBlocks';
import LatestZkEvmL2Batches from 'ui/home/LatestZkEvmL2Batches';
import Stats from 'ui/home/Stats';
import Transactions from 'ui/home/Transactions';
import ProfileMenuDesktop from 'ui/snippets/profileMenu/ProfileMenuDesktop';
import SearchBar from 'ui/snippets/searchBar/SearchBar';
import WalletMenuDesktop from 'ui/snippets/walletMenu/WalletMenuDesktop';

const rollupFeature = config.features.rollup;

const Home = () => {

  return (
    <Box as="main">
      <Flex
        w="100%"
        background="linear-gradient(135deg, rgba(12, 6, 3, 0.96), rgba(26, 10, 2, 0.90)), radial-gradient(circle at 78% 18%, rgba(255, 185, 54, 0.30), transparent 34%), radial-gradient(circle at 20% 90%, rgba(240, 90, 0, 0.20), transparent 38%)"
        borderRadius="20px"
        border="1px solid"
        borderColor="rgba(255, 185, 54, 0.24)"
        boxShadow="0 30px 110px rgba(255, 122, 26, 0.16), inset 0 1px 0 rgba(255, 236, 202, 0.12)"
        p={{ base: 4, lg: 8 }}
        columnGap={{ base: 0, lg: 10 }}
        rowGap={ 6 }
        alignItems="stretch"
        flexDir={{ base: 'column', lg: 'row' }}
        position="relative"
        overflow="hidden"
        data-label="hero plate"
        _before={{
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle, rgba(255, 216, 122, 0.50) 0 1px, transparent 2px)',
          backgroundSize: '150px 150px',
          opacity: 0.26,
          animation: 'elvorFloatLights 20s linear infinite',
          pointerEvents: 'none',
        }}
      >
        <Box flexGrow={ 1 } minW={ 0 } position="relative" zIndex={ 1 }>
          <Flex mb={{ base: 2, lg: 3 }} justifyContent="space-between" alignItems="center" columnGap={ 2 }>
            <Box>
              <Text fontSize="xs" fontWeight={ 800 } color="orange.300" letterSpacing="0" textTransform="uppercase">
                Utility & Governance Token · EVM Explorer
              </Text>
              <Heading
                as="h1"
                mt={ 2 }
                fontSize={{ base: '32px', lg: '56px' }}
                lineHeight={{ base: '36px', lg: '60px' }}
                fontWeight={ 900 }
                color={ config.UI.homepage.plate.textColor }
              >
                ELVOR <Text as="span" bgGradient="linear(to-r, elvor.100, orange.400, elvor.600)" bgClip="text">SCAN</Text>
              </Heading>
              <Text mt={ 3 } maxW="640px" color="rgba(255, 246, 232, 0.74)" fontSize={{ base: 'sm', lg: 'md' }}>
                Premium blockchain intelligence for transactions, addresses, contracts, tokens, and live network activity across the Elvor ecosystem.
              </Text>
            </Box>
            { config.UI.navigation.layout === 'vertical' && (
              <Box display={{ base: 'none', lg: 'flex' }}>
                { config.features.account.isEnabled && <ProfileMenuDesktop isHomePage/> }
                { config.features.blockchainInteraction.isEnabled && <WalletMenuDesktop isHomePage/> }
              </Box>
            ) }
          </Flex>
          <Box mt={ 6 }>
            <SearchBar isHomepage/>
          </Box>
          <Flex mt={ 5 } gap={ 3 } flexWrap="wrap">
            <Button as={ Link } href="https://dexscreener.com" isExternal variant="solid" size="sm">
              Trade ELV
            </Button>
            <Button as={ Link } href="https://elvor.io" isExternal variant="outline" size="sm">
              Elvor Token
            </Button>
          </Flex>
        </Box>
        <Box
          position="relative"
          zIndex={ 1 }
          w={{ base: '100%', lg: '360px' }}
          flexShrink={ 0 }
          border="1px solid"
          borderColor="rgba(255, 185, 54, 0.24)"
          borderRadius="20px"
          bg="rgba(6, 3, 2, 0.62)"
          boxShadow="0 22px 80px rgba(0, 0, 0, 0.38), 0 0 42px rgba(255, 122, 26, 0.18)"
          p={ 5 }
          overflow="hidden"
        >
          <Flex alignItems="center" gap={ 4 }>
            <Image src="/assets/elvor-symbol.png" alt="ELVOR Token" boxSize="86px" objectFit="contain" filter="drop-shadow(0 0 22px rgba(255, 185, 54, 0.42))"/>
            <Box>
              <Text color="orange.300" fontSize="xs" fontWeight={ 800 } textTransform="uppercase">Token Live</Text>
              <Heading as="h2" mt={ 1 } fontSize="24px" lineHeight="28px">Elvor $ELV</Heading>
              <Text color="text_secondary" fontSize="sm">Utility & Governance</Text>
            </Box>
          </Flex>
          <SimpleGrid columns={ 2 } gap={ 3 } mt={ 5 }>
            { [
              [ 'Total Supply', '10B ELV' ],
              [ 'Symbol', 'ELV' ],
              [ 'Ecosystem', 'AI + Exchange' ],
              [ 'Mode', 'Live Scan' ],
            ].map(([ label, value ]) => (
              <Box key={ label } border="1px solid" borderColor="rgba(255, 185, 54, 0.16)" borderRadius="14px" bg="rgba(255, 185, 54, 0.05)" p={ 3 }>
                <Text fontSize="10px" color="rgba(255, 236, 202, 0.52)" textTransform="uppercase" fontWeight={ 700 }>{ label }</Text>
                <Text mt={ 1 } color="white" fontWeight={ 800 }>{ value }</Text>
              </Box>
            )) }
          </SimpleGrid>
        </Box>
      </Flex>
      <Flex flexDir={{ base: 'column', lg: 'row' }} columnGap={ 2 } rowGap={ 1 } mt={ 3 } _empty={{ mt: 0 }}>
        <Stats/>
        <ChainIndicators/>
      </Flex>
      <Flex mt={ 8 } direction={{ base: 'column', lg: 'row' }} columnGap={ 12 } rowGap={ 6 }>
        { rollupFeature.isEnabled && rollupFeature.type === 'zkEvm' ? <LatestZkEvmL2Batches/> : <LatestBlocks/> }
        <Box flexGrow={ 1 }>
          <Transactions/>
        </Box>
      </Flex>
    </Box>
  );
};

export default Home;
